import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import { RatingParam } from "features/stock-ordering/core/stock-ordering.params";
import {
  PostRatingRepository,
  PostRatingResponse,
  CheckExistingRatingsRepository, // Add this import
} from "features/stock-ordering/data/stock-ordering.repository";

export enum PostRatingState {
  initial,
  inProgress,
  success,
  fail,
}

interface InitialState {
  status: PostRatingState;
  message: string;
  hasExistingRatings: boolean; // Add this
  checkingRatings: boolean; // Add this
}

const initialState: InitialState = {
  status: PostRatingState.initial,
  message: "",
  hasExistingRatings: false, // Add this
  checkingRatings: false, // Add this
};

// Add this new async thunk
export const checkExistingRatings = createAsyncThunk(
  "checkExistingRatings",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await CheckExistingRatingsRepository(orderId);
      return response;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  }
);

export const postRating = createAsyncThunk(
  "postRating",
  async (param: RatingParam, { rejectWithValue }) => {
    try {
      const response: PostRatingResponse = await PostRatingRepository(param);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  }
);

/* Main Slice */
export const postRatingSlice = createSlice({
  name: "postRating",
  initialState,
  reducers: {
    resetpostRating: (state) => {
      state.status = PostRatingState.initial;
      state.message = "Reset request";
      state.hasExistingRatings = false; // Add this
      state.checkingRatings = false; // Add this
    },
  },
  extraReducers: (builder) => {
    builder
      // Existing postRating cases
      .addCase(postRating.pending, (state) => {
        state.status = PostRatingState.inProgress;
      })
      .addCase(postRating.fulfilled, (state, action) => {
        if (action.payload) {
          const { message } = action.payload;
          state.status = PostRatingState.success;
          state.message = message;
        }
      })
      .addCase(postRating.rejected, (state, action) => {
        state.status = PostRatingState.fail;
        state.message = action.payload as string;
      })
      // Add new checkExistingRatings cases
      .addCase(checkExistingRatings.pending, (state) => {
        state.checkingRatings = true;
      })
      .addCase(checkExistingRatings.fulfilled, (state, action) => {
        state.checkingRatings = false;
        if (action.payload) {
          state.hasExistingRatings = action.payload.hasRatings || false;
        }
      })
      .addCase(checkExistingRatings.rejected, (state, action) => {
        state.checkingRatings = false;
        state.hasExistingRatings = false; // Default to no ratings on error
      });
  },
});

export const selectPostRating = (state: RootState) => state.postRating;

export const { resetpostRating } = postRatingSlice.actions;
export default postRatingSlice.reducer;
